/**
 * Local-Postgres adapter.
 *
 * Runs entirely against the JSON API served by `server/api.mjs`, which talks to
 * the Dockerised Postgres instance using the same migration, the same regex slug
 * CHECKs and the same audit trigger as production. The browser therefore gets the
 * real database behaviour (unique slugs, legal status transitions, audit trail)
 * without ever holding a database credential.
 *
 * WHO IS ACTING is not decided here. The adapter attaches the verified session
 * token and the server resolves the identity from it — there is deliberately no
 * way for a caller to claim to be someone else.
 */
import { isEmail, ROLES, ValidationError } from './repo';
import { getApiToken } from './apiToken';
import type {
  Article,
  ArticleInput,
  ArticleStatus,
  AuditEntry,
  Author,
  Category,
  Me,
  MediaItem,
  Repository,
  Role,
  Settings,
  TeamMember,
} from './repo';

const API_BASE = ((import.meta.env?.VITE_API_BASE as string | undefined) ?? '').replace(/\/+$/, '');

interface CallOptions {
  method?: string;
  body?: unknown;
  /** A 404 is a legitimate "no such row" answer, not a transport error. */
  nullOn404?: boolean;
  /**
   * Statuses that mean "not signed in / not allowed" rather than a failure.
   * Only used by `getMe`, where an anonymous visitor is the normal case.
   */
  nullOn?: number[];
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function errorMessage(payload: unknown, status: number): string {
  if (payload && typeof payload === 'object') {
    const rec = payload as Record<string, unknown>;
    if (typeof rec.error === 'string' && rec.error) return rec.error;
  }
  return `Request failed (${status})`;
}

/** Perform one API call, translating the server's error shapes into the client's. */
async function call<T>(path: string, opts: CallOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  // Every request carries the session token when there is one: public reads
  // simply ignore it, and protected routes need it. The token is read per call
  // because Clerk session tokens are short-lived.
  const token = await getApiToken();
  if (token) headers.authorization = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch (err) {
    throw new Error(err instanceof Error ? `Could not reach the API: ${err.message}` : 'Could not reach the API');
  }

  if (res.status === 404 && opts.nullOn404) return null as T;
  if (opts.nullOn?.includes(res.status)) return null as T;

  if (res.status === 401) throw new Error('Your session has expired. Please sign in again.');
  if (res.status === 403) {
    const payload = await readJson(res);
    throw new Error(errorMessage(payload, 403));
  }

  if (res.status === 422) {
    const payload = await readJson(res);
    if (payload && typeof payload === 'object') {
      const errors = (payload as Record<string, unknown>).errors;
      if (errors && typeof errors === 'object') throw new ValidationError(errors as Record<string, string>);
    }
    throw new Error(errorMessage(payload, 422));
  }

  if (!res.ok) throw new Error(errorMessage(await readJson(res), res.status));
  if (res.status === 204) return undefined as T;
  return (await readJson(res)) as T;
}

/** Build a query string, omitting unset/empty values so the server sees clean URLs. */
function query(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}

/**
 * The API contract is `roles: Role[]`, and the server once broke it: the SQL
 * function returned a Postgres ENUM array, which node-postgres passes through as
 * the raw literal `{admin}`. `String.prototype.some` does not exist, so the
 * admin guard threw during render and a signed-in administrator saw a blank
 * page rather than an error.
 *
 * The server side is fixed and covered by a test on the SQL layer. This is the
 * second layer: if the shape ever slips again the UI degrades to the honest 403
 * screen instead of crashing, and the warning keeps it visible rather than
 * silently papering over it.
 */
function asStringArray(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
    console.warn(
      '[repo] roles/permissions arrived as a Postgres array literal; the API contract is an array.',
    );
    const inner = value.slice(1, -1);
    return inner.length ? inner.split(',').map((s) => s.replace(/^"|"$/g, '').trim()) : [];
  }
  return [];
}

/** Unknown role names are dropped, not admitted: permission checks fail closed. */
function asRoles(value: unknown): Role[] {
  return asStringArray(value).filter((v): v is Role => (ROLES as readonly string[]).includes(v));
}

function normalizeMe(me: Me): Me {
  return { ...me, roles: asRoles(me.roles), permissions: asStringArray(me.permissions) };
}

export class HttpRepository implements Repository {
  readonly kind = 'http' as const;

  /** Who the server says I am — roles and permissions included. */
  async getMe(): Promise<Me | null> {
    const me = await call<Me | null>('/api/me', { nullOn: [401, 403] });
    return me ? normalizeMe(me) : null;
  }

  async listCategories(): Promise<Category[]> {
    return call<Category[]>('/api/categories');
  }

  async getCategoryBySlug(slug: string): Promise<Category | null> {
    return call<Category | null>(`/api/categories/${encodeURIComponent(slug)}`, { nullOn404: true });
  }

  async listAuthors(): Promise<Author[]> {
    return call<Author[]>('/api/authors');
  }

  async listPublicArticles(
    opts: { categoryId?: string; authorId?: string; query?: string; limit?: number; offset?: number } = {},
  ): Promise<Article[]> {
    return call<Article[]>(
      `/api/articles${query({
        scope: 'public',
        categoryId: opts.categoryId,
        authorId: opts.authorId,
        q: opts.query,
        limit: opts.limit,
        offset: opts.offset,
      })}`,
    );
  }

  async getPublicArticleBySlug(slug: string): Promise<Article | null> {
    return call<Article | null>(`/api/articles/slug/${encodeURIComponent(slug)}`, { nullOn404: true });
  }

  async listAllArticles(opts: { status?: ArticleStatus | 'all'; query?: string } = {}): Promise<Article[]> {
    return call<Article[]>(
      `/api/articles${query({ scope: 'all', status: opts.status, q: opts.query })}`,
    );
  }

  async getArticleById(id: string): Promise<Article | null> {
    return call<Article | null>(`/api/articles/${encodeURIComponent(id)}`, { nullOn404: true });
  }

  async saveArticle(input: ArticleInput): Promise<Article> {
    if (input.id) return call<Article>(`/api/articles/${encodeURIComponent(input.id)}`, { method: 'PUT', body: input });
    // The server derives and enforces the slug; never compute one here.
    return call<Article>('/api/articles', { method: 'POST', body: input });
  }

  async deleteArticle(id: string): Promise<void> {
    await call<void>(`/api/articles/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listMedia(): Promise<MediaItem[]> {
    return call<MediaItem[]>('/api/media');
  }

  async addMedia(item: Omit<MediaItem, 'id' | 'createdAt'>): Promise<MediaItem> {
    return call<MediaItem>('/api/media', { method: 'POST', body: item });
  }

  async updateMedia(id: string, patch: { altText?: string; credit?: string }): Promise<MediaItem> {
    return call<MediaItem>(`/api/media/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
  }

  async listTeam(): Promise<TeamMember[]> {
    return call<TeamMember[]>('/api/team');
  }

  async inviteMember(email: string, role: Role): Promise<void> {
    await call<void>('/api/team/invite', { method: 'POST', body: { email, role } });
  }

  async removeMember(userId: string, role: Role): Promise<void> {
    await call<void>(`/api/team/${encodeURIComponent(userId)}${query({ role })}`, { method: 'DELETE' });
  }

  async saveCategory(
    input: { id?: string; name: string; slug?: string; description?: string; sortOrder?: number },
  ): Promise<Category> {
    if (input.id) {
      return call<Category>(`/api/categories/${encodeURIComponent(input.id)}`, { method: 'PUT', body: input });
    }
    return call<Category>('/api/categories', { method: 'POST', body: input });
  }

  async deleteCategory(id: string): Promise<void> {
    await call<void>(`/api/categories/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listAuditLog(): Promise<AuditEntry[]> {
    return call<AuditEntry[]>('/api/audit');
  }

  async subscribe(email: string): Promise<{ ok: boolean; error?: string }> {
    if (!isEmail(email)) return { ok: false, error: 'Enter a valid email address.' };
    const headers: Record<string, string> = { accept: 'application/json', 'content-type': 'application/json' };
    const token = await getApiToken();
    if (token) headers.authorization = `Bearer ${token}`;

    const res = await fetch(`${API_BASE}/api/newsletter`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ email }),
    });
    if (res.ok) return { ok: true };
    const payload = await readJson(res);
    const message =
      payload && typeof payload === 'object' && typeof (payload as Record<string, unknown>).error === 'string'
        ? ((payload as Record<string, unknown>).error as string)
        : 'Could not subscribe right now.';
    return { ok: false, error: message };
  }

  async getSettings(): Promise<Settings> {
    return call<Settings>('/api/settings');
  }

  async saveSettings(next: Settings): Promise<Settings> {
    return call<Settings>('/api/settings', { method: 'PUT', body: next });
  }
}
